import { NotificationTypeIds, SourceApp, type INotification } from '@common/notification-layer/types/notification.types'

import {
  EVENTS_CONNECTION_PARTICIPANTS_LIMIT,
  EVENTS_CONNECTION_STATUSES_LIMIT,
  type EventConnectionAction,
  type EventConnectionStatus,
  type EventParticipantConnectionAction,
  type EventParticipantReference,
  type EventParticipantRole,
} from '../types/events-management'
import type { EventConnectionNotificationState } from '../types/use-event-connections'

const participantKey = (participant: EventParticipantReference) => `${participant.role}:${participant.participantId}`

/** The participants the portal is asked about, capped at one request's worth. */
export const toParticipantReferences = (
  role: EventParticipantRole,
  items: readonly { readonly id: string }[]
): EventParticipantReference[] => items.slice(0, EVENTS_CONNECTION_PARTICIPANTS_LIMIT).map(item => ({ role, participantId: item.id }))

/** Attaches the viewer's connection action to each item; a participant the portal did not answer for cannot be connected with yet. */
export const withConnectionActions = <TItem extends { readonly id: string }>(
  items: readonly TItem[],
  role: EventParticipantRole,
  actions: readonly EventParticipantConnectionAction[] | undefined
): Array<TItem & { readonly connectionAction: EventConnectionAction }> => {
  const byParticipant = new Map((actions ?? []).map(action => [participantKey(action.participant), action] as const))
  return items.map(item => {
    const action = byParticipant.get(participantKey({ role, participantId: item.id }))
    if (!action) return { ...item, connectionAction: { state: 'unavailable' } }
    const { participant: _participant, ...connectionAction } = action
    return { ...item, connectionAction }
  })
}

/**
 * Normalizes a request version from any transport. Push data can only carry strings, and the in-app
 * record is built from the same flattened metadata as push, so a stored notification holds
 * requestVersion as "1" while the connection status API returns 1.
 */
export const asPositiveInteger = (value: unknown): number | null => {
  const normalized = typeof value === 'string' && /^[1-9]\d*$/.test(value) ? Number.parseInt(value, 10) : value
  return typeof normalized === 'number' && Number.isSafeInteger(normalized) && normalized > 0 ? normalized : null
}

export const isEventConnectionRequestNotification = (notification: INotification) =>
  notification.sourceApp === SourceApp.EVENTS &&
  (notification.type ?? notification.notificationType) === NotificationTypeIds.EVENT_CONNECTION_REQUESTED

/**
 * Connection ids for one status request. Every row in a chunk shares this sorted slice, so the status query
 * key matches and TanStack sends one request per chunk. The row's own id stays inside its chunk.
 */
export const toNotificationConnectionIds = (connectionId: string, notifications: readonly INotification[]): string[] => {
  const currentId = connectionId.trim()
  const ids = [
    ...new Set(
      [
        currentId,
        ...notifications.filter(isEventConnectionRequestNotification).map(item => item.metaData?.connectionId?.trim() ?? ''),
      ].filter(Boolean)
    ),
  ].sort()
  const index = ids.indexOf(currentId)
  if (index < 0) return []
  const start = Math.floor(index / EVENTS_CONNECTION_STATUSES_LIMIT) * EVENTS_CONNECTION_STATUSES_LIMIT
  return ids.slice(start, start + EVENTS_CONNECTION_STATUSES_LIMIT)
}

/**
 * What a connection-request notification shows. A notification for an older request version than the connection's
 * current one was superseded, and reads as declined.
 */
export const toConnectionNotificationState = (
  connection: EventConnectionStatus | undefined,
  notificationRequestVersion: number | undefined
): EventConnectionNotificationState => {
  const currentVersion = connection?.requestVersion
  const hasBothVersions = notificationRequestVersion !== undefined && currentVersion !== undefined
  if (hasBothVersions && notificationRequestVersion < currentVersion) return 'declined'
  if (!connection || (hasBothVersions && notificationRequestVersion != currentVersion)) return 'unknown' // TODO: backend sends string/int for request version in status api
  if (connection.status === 'pending') return connection.direction === 'incoming' ? 'incoming_pending' : 'unknown'
  return connection.status
}
